import * as Y from 'yjs';
import { newDataKey, openText, sealText } from '../crypto/vault.ts';
import { unwrapDataKey, wrapDataKey } from '../crypto/session.ts';
import { store } from './db.ts';
import { acquire, release, withDoc } from './docs.ts';
import { parseFrontmatter } from './markdown.ts';
import type { LocalNote } from './types.ts';

export function newId(): string {
  return crypto.randomUUID();
}

export function encryptWithNoteKey(text: string, wrappedDek: string): string {
  return sealText(text, unwrapDataKey(wrappedDek));
}

export function decryptWithNoteKey(payload: string, wrappedDek: string): string {
  return openText(payload, unwrapDataKey(wrappedDek));
}

export function emptyNote(id: string, wrappedDek: string, now: number): LocalNote {
  return {
    id,
    content: '',
    pinned: false,
    folderId: null,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    wrappedDek,
    state: null,
    seq: 0,
    sinceSnapshot: 0,
    dirty: false,
    dokument: null,
  };
}

export async function createNote(initial = '', folderId: string | null = null): Promise<LocalNote> {
  const now = Date.now();
  const id = newId();
  // Die Zeile muss vor dem ersten Delta stehen: der Server nimmt keine Updates für
  // Notizen an, die er nicht kennt.
  const note: LocalNote = { ...emptyNote(id, wrapDataKey(newDataKey()), now), dirty: true };
  await store.putNote(note);

  const entry = await acquire(id);
  entry.doc.transact(() => {
    entry.meta.set('createdAt', now);
    entry.meta.set('pinned', false);
    entry.meta.set('folderId', folderId);
    if (initial) entry.text.insert(0, initial);
  });
  release(id);
  return note;
}

export const notes = {
  /** Ersetzt den gesamten Text — für das Wiederherstellen aus dem Verlauf. */
  setContent: (id: string, content: string) =>
    withDoc(id, (entry) => {
      entry.doc.transact(() => {
        entry.text.delete(0, entry.text.length);
        entry.text.insert(0, content);
      });
    }),

  setPinned: (id: string, pinned: boolean) =>
    withDoc(id, (entry) => entry.meta.set('pinned', pinned)),

  setFolder: (id: string, folderId: string | null) =>
    withDoc(id, (entry) => entry.meta.set('folderId', folderId)),

  async remove(id: string): Promise<void> {
    const now = Date.now();
    await store.patchNote(id, (note) => ({ ...note, deletedAt: now, updatedAt: now, dirty: true }));
  },

  async restore(id: string): Promise<void> {
    const now = Date.now();
    await store.patchNote(id, (note) => ({ ...note, deletedAt: null, updatedAt: now, dirty: true }));
  },
};

/**
 * Schreibt eine Frontmatter-Eigenschaft. Ersetzt wird nur der Frontmatter-Bereich,
 * nicht das ganze Dokument — sonst würde jede Zelländerung in einer Datenbanktabelle
 * den gesamten Text als geändert durch den Abgleich schicken.
 */
export async function setProperty(id: string, key: string, value: string): Promise<void> {
  await withDoc(id, (entry) => {
    const current = parseFrontmatter(entry.text.toString());
    const data: Record<string, string> = { ...current.data };
    if (value.trim() === '') delete data[key];
    else data[key] = value.trim();

    const keys = Object.keys(data);
    const block =
      keys.length === 0 ? '' : `---\n${keys.map((name) => `${name}: ${data[name]}`).join('\n')}\n---\n`;
    if (block === current.raw) return;

    entry.doc.transact(() => {
      if (current.raw.length > 0) entry.text.delete(0, current.raw.length);
      if (block.length > 0) entry.text.insert(0, block);
    });
  });
}

/** Text einer Notiz, die gerade nicht offen ist — ohne den Spiegel zu bemühen. */
export async function readContent(id: string): Promise<string> {
  return withDoc(id, (entry) => entry.text.toString());
}

export { Y };
